import { trackRegistrationComplete } from '@/common/analytics/gtm/trackRegistrationComplete';
import { trackUserLogin } from '@/common/analytics/gtm/trackUserLogin';
import { trackUserLogout } from '@/common/analytics/gtm/trackUserLogout';

export type loginMethod = {
  loginMethod: string;
};

export type registrationMethod = {
  registrationMethod: string;
};

type TrackingStackEvent =
  | 'trackRegistrationComplete'
  | 'trackUserLogout'
  | 'trackUserLogin'
  | 'login';

export interface ClientStackConfigProps {
  name: string;
  handler: Record<TrackingStackEvent, ((data: never) => void) | (() => void)>;
}

export interface ClientStackItemProps<DataType> {
  event: TrackingStackEvent;
  data?: DataType;
}

interface ClientStackMethods {
  consumeClientStackItems: (itemNames: TrackingStackEvent[]) => void;
  addClientStackItem: <DataType>(item: ClientStackItemProps<DataType>) => void;
}

const trackingConfig: ClientStackConfigProps = {
  name: 'tracking-stack',
  handler: {
    trackRegistrationComplete: (data: registrationMethod): void => trackRegistrationComplete(data),
    trackUserLogout: (): void => trackUserLogout(),
    trackUserLogin: (data: loginMethod): void => trackUserLogin(data),
    login: (data: loginMethod): void => trackUserLogin(data),
  },
};

export const useClientStack = (
  config: ClientStackConfigProps = trackingConfig,
): ClientStackMethods => {
  const getStackItems = (): ClientStackItemProps<never>[] => {
    if (!globalThis.sessionStorage) return [];

    const stackItems = JSON.parse(sessionStorage.getItem(config.name) || '[]');

    return stackItems;
  };

  const addClientStackItem = <DataType>(item: ClientStackItemProps<DataType>): void => {
    const stackItems = getStackItems();

    stackItems.push(item as ClientStackItemProps<never>);

    sessionStorage.setItem(config.name, JSON.stringify(stackItems));
  };

  const consumeClientStackItems = (itemNames: TrackingStackEvent[]): void => {
    const stackItems = getStackItems();

    const stackItemsToPersist: ClientStackItemProps<never>[] = [];

    const filteredStackItems = stackItems?.map((stackItem) => {
      if (!itemNames.includes(stackItem.event)) stackItemsToPersist.push(stackItem);
      else return stackItem;
    });

    if (filteredStackItems.length) {
      do {
        const stackItem = filteredStackItems.pop();
        if (stackItem && config.handler[stackItem.event]) {
          const handler = config.handler[stackItem.event];
          stackItem.data ? handler(stackItem.data) : (handler as () => void)();
        }
      } while (filteredStackItems.length);
    }

    if (stackItemsToPersist.length > 0)
      sessionStorage.setItem(config.name, JSON.stringify(stackItemsToPersist));
    else sessionStorage.removeItem(config.name);
  };

  return {
    addClientStackItem,
    consumeClientStackItems,
  };
};
